"use client";

import { LucideIcon } from "lucide-react";
import { ArrowRight } from "lucide-react";

interface ServiceCardProps {
  icon: LucideIcon;
  title: string;
  description: string;
  delay?: number;
  onLearnMore?: () => void;
}

/**
 * ServiceCard Component
 * Displays a single service with an icon, title, description, and a "Learn more" link.
 * The card is interactive and accessible, supporting both mouse and keyboard interactions.
 */
export function ServiceCard({
  icon: Icon,
  title,
  description,
  delay = 0,
  onLearnMore,
}: ServiceCardProps) {
  // Handle keyboard interaction for accessibility
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (onLearnMore && (e.key === "Enter" || e.key === " ")) {
      e.preventDefault();
      onLearnMore();
    }
  };

  return (
    <div
      onClick={onLearnMore}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label={`Learn more about ${title}`}
      className="group from-card to-card/80 border-black/40 md:border-black/20 animate-fade-in-up hover:shadow-primary/10 hover:border-primary/30 focus:ring-primary relative cursor-pointer 
      overflow-hidden rounded-2xl border bg-gradient-to-br p-6 opacity-0 transition-all duration-500 hover:-translate-y-2 hover:shadow-2xl focus:ring-2 focus:ring-offset-2 focus:outline-none"
      style={{ animationDelay: `${delay}ms`, animationFillMode: "forwards" }}
    >
      {/* Background gradient on hover */}
      <div className="from-primary/5 to-primary/10 absolute inset-0 bg-gradient-to-br via-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100" />

      {/* Decorative circle */}
      <div className="bg-primary/5 group-hover:bg-primary/10 absolute -top-10 -right-10 h-32 w-32 rounded-full blur-2xl transition-all duration-500" />

      {/* Content */}
      <div className="relative z-10">
        {/* Icon container with premium styling */}
        <div className="relative mb-5">
          <div className="from-primary/20 to-primary/10 text-primary group-hover:from-primary group-hover:to-primary/80 group-hover:text-primary-foreground shadow-primary/10 group-hover:shadow-primary/30 inline-flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br shadow-lg transition-all duration-500 group-hover:scale-110">
            <Icon className="h-7 w-7" aria-hidden="true" />
          </div>
          {/* Glow effect */}
          <div className="bg-primary/20 absolute inset-0 h-14 w-14 rounded-xl opacity-0 blur-xl transition-opacity duration-500 group-hover:opacity-60" />
        </div>

        {/* Title */}
        <h3 className="group-hover:text-primary mb-3 text-lg font-semibold transition-colors duration-300">
          {title}
        </h3>

        {/* Description */}
        <p className="text-muted-foreground mb-4 text-sm leading-relaxed">
          {description}
        </p>

        {/* Learn more link */}
        <div
          className="text-primary flex translate-x-[-10px] items-center gap-1.5 text-sm font-medium opacity-0 transition-all duration-300 group-hover:translate-x-0 group-hover:opacity-100"
          aria-hidden="true" // Hidden from screen readers as the whole card is interactive
        >
          <span>Learn more</span>
          <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
        </div>
      </div>

      {/* Bottom accent line */}
      <div className="via-primary/50 absolute right-0 bottom-0 left-0 h-1 scale-x-0 bg-gradient-to-r from-transparent to-transparent transition-transform duration-500 group-hover:scale-x-100" />
    </div>
  );
}
